import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ENOPCaseStatus,
  type CaseClusterType,
  type NewCaseClusterType,
} from '#entities';

import { useQueryHubsResourceReport } from '../../../api';
import { useAircrafts, useData } from '../../../data-context';
import { useGeoMap } from '../../../GeoMap';
import { getCaseClustering, getCasesPath } from '../../../helpers';

export const useMainMapOverlay = () => {
  const {
    filteredActiveCases: cases,
    filteredNewCases: newCases,
    lastUpdateTime,
  } = useData();
  const { data: hubs } = useQueryHubsResourceReport();

  // Unique namespace per map instance (IDs in <svg> are document-global)

  const { interactionLayerRef, projectionRef, onChangeEnd, bounds } =
    useGeoMap();

  const casesToRender = useMemo(
    () => cases.filter(c => c.status !== ENOPCaseStatus.ReturningToBase),
    [cases],
  );

  const hasContent = Boolean(
    hubs.length || casesToRender.length || newCases.length,
  );

  // TODO: get rid of clusters and use cases instead
  const [clusters, setClusters] = useState<
    Array<CaseClusterType | NewCaseClusterType>
  >([]);

  const newCasesOnMap = useMemo(
    () => newCases.filter(item => item.showOnMap),
    [newCases],
  );

  const aircrafts = useAircrafts();

  const aircraftsOnFlight = useMemo(
    () => aircrafts.filter(aircraft => aircraft.isOnFlight),
    [aircrafts],
  );

  const recomputeRef = useRef<() => void>(() => {});
  const viewVersionRef = useRef(0);

  const recompute = useCallback(() => {
    if (!hasContent || !bounds) {
      setClusters(prev => (prev.length ? [] : prev));
      return;
    }
    const casesWithPath = getCasesPath(
      casesToRender,
      projectionRef.current,
      lastUpdateTime as Date,
    );
    const data = getCaseClustering({
      cases: casesWithPath,
      newCases,
      clientHeight: bounds.height,
      clientWidth: bounds.width,
    }) as Array<NewCaseClusterType | CaseClusterType>;
    setClusters(() => {
      const v = viewVersionRef.current;
      return data.map(c => ({ ...c, __viewVersion: v }));
    });
  }, [
    bounds,
    casesToRender,
    hasContent,
    lastUpdateTime,
    newCases,
    projectionRef,
  ]);

  recomputeRef.current = recompute;

  useEffect(() => {
    recompute();
  }, [
    hasContent,
    bounds?.height,
    bounds?.width,
    casesToRender,
    newCases,
    lastUpdateTime,
    projectionRef,
    recompute,
  ]);

  // Subscribe to `onChangeEnd` to recompute clusters after map view changes.
  // viewVersionRef is incremented here so setClusters captures the correct version.
  useEffect(
    () =>
      onChangeEnd(() => {
        viewVersionRef.current++;
        recompute();
      }),
    [onChangeEnd, recompute],
  );

  return {
    hasContent,
    interactionLayerRef,
    newCasesOnMap,
    cases: casesToRender,
    aircraftsOnFlight,
    clusters,
    hubs,
  };
};
